import { accessibleName, computeRole } from "./roles.js";
/**
 * The safety layer decides — in code, before any DOM mutation — whether an
 * action may proceed, must be confirmed, or is outright denied. Model output
 * can never bypass this; see docs/PRD.md FR20–FR25.
 */
export class SafetyLayer {
    policy;
    constructor(policy) {
        this.policy = policy;
    }
    /** Zone check: is the element inside a denied region (or outside allowOnly)? */
    zoneBlocked(el) {
        const doc = el.ownerDocument;
        for (const sel of this.policy.zones.deny) {
            for (const denied of Array.from(doc.querySelectorAll(sel))) {
                if (denied === el || denied.contains(el))
                    return true;
            }
        }
        const allow = this.policy.zones.allowOnly;
        if (allow?.length) {
            const inside = allow.some((sel) => Array.from(doc.querySelectorAll(sel)).some((a) => a === el || a.contains(el)));
            if (!inside)
                return true;
        }
        return false;
    }
    /** Never allow typing into (or reading back) password fields programmatically. */
    isSecretField(el) {
        return (el.tagName.toLowerCase() === "input" &&
            el.type === "password");
    }
    /** Decide gating for a click on `el`. */
    gateClick(el) {
        if (this.zoneBlocked(el))
            return { allowed: false, needsConfirm: false, reason: "element is in a denied zone" };
        const role = computeRole(el);
        const name = accessibleName(el);
        const c = this.policy.confirm;
        // A bare <button> defaults to type="submit" per the DOM spec, but only
        // actually submits when inside a form. Gate on explicit intent OR real
        // submit behavior — not on every type-less button.
        const tag = el.tagName.toLowerCase();
        const typeAttr = el.getAttribute("type");
        const explicitSubmit = typeAttr === "submit";
        const bareButtonInForm = tag === "button" && !typeAttr && el.closest("form") != null;
        const isSubmit = explicitSubmit || bareButtonInForm;
        const isExternalNav = el.tagName.toLowerCase() === "a" &&
            isExternalHref(el.href, el.ownerDocument);
        const matchesDeny = c.denyNamePatterns.some((re) => re.test(name));
        const danger = /\b(delete|remove|deactivate|cancel|close\s+account)\b/i.test(name);
        if ((c.submits && isSubmit) ||
            (c.externalNav && isExternalNav) ||
            matchesDeny) {
            return {
                allowed: true,
                needsConfirm: true,
                danger,
                reason: describeReason({ isSubmit, isExternalNav, matchesDeny, name, role }),
            };
        }
        return { allowed: true, needsConfirm: false };
    }
    /**
     * Run a gated action: enforce zone/confirm decisions, prompting the user
     * through `confirm` when required. Returns a ToolError if blocked/declined,
     * else null (proceed).
     */
    async enforce(decision, confirm, label) {
        if (!decision.allowed) {
            return { error: { code: "denied", message: decision.reason ?? "action denied by policy" } };
        }
        if (decision.needsConfirm) {
            const answer = await confirm({
                question: `Allow Quill to ${label}?${decision.reason ? " " + decision.reason : ""}`,
                kind: "confirm",
                danger: decision.danger,
            });
            const yes = /^\s*(y|yes|allow|ok|okay|confirm|sure|do it)\b/i.test(answer);
            if (!yes) {
                return { error: { code: "denied", message: "user declined the action" } };
            }
        }
        return null;
    }
}
function isExternalHref(href, doc) {
    try {
        const u = new URL(href, doc.baseURI);
        if (u.protocol !== "http:" && u.protocol !== "https:")
            return false;
        return u.origin !== doc.location.origin;
    }
    catch {
        return false;
    }
}
function describeReason(x) {
    if (x.isSubmit)
        return "This submits a form.";
    if (x.isExternalNav)
        return "This navigates to another site.";
    if (x.matchesDeny)
        return `"${x.name}" looks like a sensitive action.`;
    return "";
}
//# sourceMappingURL=safety.js.map